/**
 * Orders destination — what the operator has narrowed the event's order list
 * down to, and the list that leaves.
 *
 * THREE CELLS, NOT ONE RECORD. `computed` tracks whole cells, so a single
 * `{query, facet, dateRange}` record would invalidate the date-window pass and
 * the facet counts on every keystroke in the search box — which is the whole
 * cost this file exists to remove. Split, a keystroke reaches exactly one
 * derivation.
 *
 * THE ORDER OF THE PASSES IS THE CHIPS' OWN MEANING. Date window, then facet,
 * then search: the window and the facet are both scoped off the view model's
 * own orders so a chip's count stays comparable with its siblings, and the
 * search box narrows whatever that leaves.
 *
 * Written by orders.view.ts alone, which clears all three on unmount — the
 * cells outlive the element, and a filter carried back into a destination the
 * operator left would state a narrowed list as the event's whole order set.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import { consoleViewModel } from "./console.state.ts";
import {
	facetCounts,
	filterOrdersByDateRange,
	filterOrdersByFacet,
	filterOrdersByQuery,
	type OrderFacet,
} from "./order-detail.engine.ts";

export const consoleOrdersQuery = state<string>(
	"events/console/orders-query",
).memory("");

/** `""` is the chip group's own deselected state rather than a fourth facet —
 *  see `OrderFacet`'s own note. */
export const consoleOrdersFacet = state<OrderFacet | "">(
	"events/console/orders-facet",
).memory("");

export const consoleOrdersDateRange = state<{
	readonly start: string | null;
	readonly end: string | null;
}>("events/console/orders-date-range").memory({ start: null, end: null });

const ordersInDateRange = computed(() => {
	const { start, end } = consoleOrdersDateRange.value;
	return filterOrdersByDateRange(
		start,
		end,
		consoleViewModel.get()?.orders ?? [],
	);
});

/** Each facet's own size within the picked date window — read by the chips,
 *  and untouched by the search box, which is what keeps a chip's number the
 *  size of what pressing it would show. */
export const consoleOrderFacetCounts = computed(() =>
	facetCounts(ordersInDateRange.get()),
);

const ordersInFacet = computed(() =>
	filterOrdersByFacet(consoleOrdersFacet.value, ordersInDateRange.get()),
);

/** The list the destination renders. */
export const consoleOrdersPage = computed(() =>
	filterOrdersByQuery(
		consoleOrdersQuery.value,
		ordersInFacet.get(),
		consoleViewModel.get()?.ticketsByOrder,
	),
);
